import React, { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faMapMarkerAlt,
  faPlusCircle,
  faShareAlt,
  faInfoCircle,
  faTimes,
  faGraduationCap,
  faBuilding,
  faLaptop,
  faCouch,
  faCar,
  faPlaneDeparture,
  faTshirt,
  faUtensils,
  faGlobe,
  faPercentage,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

const categories = [
  { name: "All Categories", icon: faGlobe },
  { name: "Education", icon: faGraduationCap },
  { name: "Real Estate", icon: faBuilding },
  { name: "Electronics", icon: faLaptop },
  { name: "Furniture & Home", icon: faCouch },
  { name: "Automobiles", icon: faCar },
  { name: "Travel", icon: faPlaneDeparture },
  { name: "Fashion", icon: faTshirt },
  { name: "Groceries & Kitchen", icon: faUtensils },
];

const initialDeals = [
  {
    id: 1,
    category: "Education",
    title: "School Uniforms Bulk Deal 2026",
    description: "Premium cotton uniforms for primary & secondary schools",
    location: "Bangalore",
    currentBuyers: 68,
    targetBuyers: 100,
    estimatedSavings: "20-25%",
    stage: "Open for Joining",
  },
  {
    id: 2,
    category: "Real Estate",
    title: "2BHK in Prestige Lakeview Project",
    description: "Group booking for Phase 2 launch with extra amenities",
    location: "Mumbai",
    currentBuyers: 42,
    targetBuyers: 60,
    estimatedSavings: "12-18% + free parking",
    stage: "Open for Joining",
  },
  {
    id: 3,
    category: "Electronics",
    title: "Dell Inspiron Laptops (i5, 16GB RAM)",
    description: "Student & professional models with 3-year warranty",
    location: "Nationwide",
    currentBuyers: 138,
    targetBuyers: 150,
    estimatedSavings: "18-22%",
    stage: "Threshold Met - Negotiating",
  },
  {
    id: 4,
    category: "Furniture & Home",
    title: "Modular Kitchen Setup",
    description: "Complete L-shaped kitchen with chimney & hob",
    location: "Delhi NCR",
    currentBuyers: 22,
    targetBuyers: 40,
    estimatedSavings: "25-35%",
    stage: "Open for Joining",
  },
  {
    id: 5,
    category: "Automobiles",
    title: "Hyundai Creta SX Variant",
    description: "2026 model group booking with accessories package",
    location: "Chennai",
    currentBuyers: 15,
    targetBuyers: 30,
    estimatedSavings: "8-12% + free insurance",
    stage: "Open for Joining",
  },
  {
    id: 6,
    category: "Travel",
    title: "Goa Family Vacation Package",
    description: "5-star resort, flights & meals for 4 nights",
    location: "Nationwide",
    currentBuyers: 85,
    targetBuyers: 100,
    estimatedSavings: "30-40%",
    stage: "Open for Joining",
  },
  {
    id: 7,
    category: "Fashion",
    title: "Winter Jackets Collection",
    description: "Branded puffer & leather jackets",
    location: "North India",
    currentBuyers: 55,
    targetBuyers: 80,
    estimatedSavings: "35%",
    stage: "Open for Joining",
  },
  {
    id: 8,
    category: "Groceries & Kitchen",
    title: "Annual Grocery Essentials Pack",
    description: "Rice, oils, spices, pulses - 1 year supply",
    location: "Hyderabad",
    currentBuyers: 28,
    targetBuyers: 50,
    estimatedSavings: "15-20%",
    stage: "Open for Joining",
  },
  {
    id: 9,
    category: "Electronics",
    title: "65-inch 4K Smart TVs",
    description: "Samsung & LG QLED models",
    location: "Nationwide",
    currentBuyers: 92,
    targetBuyers: 100,
    estimatedSavings: "20-28%",
    stage: "Threshold Met - Negotiating",
  },
  {
    id: 10,
    category: "Real Estate",
    title: "Villa Plots in Gated Community",
    description: "40x60 plots near airport",
    location: "Pune",
    currentBuyers: 8,
    targetBuyers: 25,
    estimatedSavings: "10-15%",
    stage: "Open for Joining",
  },
];

export default function UnityDealsHome() {
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [searchLocation, setSearchLocation] = useState("");
  const [deals, setDeals] = useState(initialDeals);
  const [showAbout, setShowAbout] = useState(false);
  const [showCustomDeal, setShowCustomDeal] = useState(false);
  const [customForm, setCustomForm] = useState({
    category: "",
    title: "",
    location: "",
    target: "50",
    description: "",
  });

  const totalBuyers = deals.reduce((sum, deal) => sum + deal.currentBuyers, 0);

  const filteredDeals = deals.filter((deal) => {
    const matchesCategory =
      selectedCategory === "All Categories" || deal.category === selectedCategory;
    const matchesLocation =
      searchLocation === "" ||
      deal.location.toLowerCase().includes(searchLocation.toLowerCase());
    return matchesCategory && matchesLocation;
  });

  const handleJoin = (id) => {
    setDeals((prev) =>
      prev.map((deal) => {
        if (deal.id === id && deal.stage === "Open for Joining") {
          const newCount = deal.currentBuyers + 1;
          const newStage =
            newCount >= deal.targetBuyers
              ? "Threshold Met - Negotiating"
              : "Open for Joining";
          return { ...deal, currentBuyers: newCount, stage: newStage };
        }
        return deal;
      })
    );
  };

  const handleShare = (deal) => {
    if (navigator.share) {
      navigator.share({
        title: `UnityDeals: ${deal.title}`,
        text: `Join ${deal.currentBuyers}/${deal.targetBuyers} buyers for ${deal.estimatedSavings} savings!`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Deal link copied! Share it to bring more buyers.");
    }
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (!customForm.category || !customForm.title) {
      alert("Category and title are required.");
      return;
    }

    const newDeal = {
      id: Date.now(),
      category: customForm.category,
      title: customForm.title,
      description: customForm.description || "Custom group deal initiated by you",
      location: customForm.location || "Nationwide",
      currentBuyers: 1,
      targetBuyers: parseInt(customForm.target) || 50,
      estimatedSavings: "15-30%",
      stage: "Open for Joining",
    };

    setDeals((prev) => [...prev, newDeal]);
    setShowCustomDeal(false);
    setCustomForm({ category: "", title: "", location: "", target: "50", description: "" });
  };

  const DealCard = ({ deal }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
      if (!chartRef.current) return;

      const percentage = (deal.currentBuyers / deal.targetBuyers) * 100;
      const remaining = Math.max(0, deal.targetBuyers - deal.currentBuyers);

      if (!chartInstanceRef.current) {
        chartInstanceRef.current = new Chart(chartRef.current, {
          type: "doughnut",
          data: {
            datasets: [
              {
                data: [deal.currentBuyers, remaining],
                backgroundColor:
                  percentage >= 100
                    ? ["#10b981", "#064e3b"]
                    : ["#3b82f6", "#1e293b"],
                borderWidth: 0,
                borderRadius: 8,
              },
            ],
          },
          options: {
            cutout: "75%",
            plugins: {
              legend: { display: false },
              tooltip: { enabled: false },
            },
            responsive: true,
            maintainAspectRatio: false,
            animation: { animateRotate: true, duration: 1200 },
          },
        });
      } else {
        const chart = chartInstanceRef.current;
        chart.data.datasets[0].data = [deal.currentBuyers, remaining];
        chart.data.datasets[0].backgroundColor =
          percentage >= 100 ? ["#10b981", "#064e3b"] : ["#3b82f6", "#1e293b"];
        chart.update();
      }
    }, [deal.currentBuyers, deal.targetBuyers]);

    useEffect(() => {
      return () => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }
      };
    }, []);

    const isOpen = deal.stage === "Open for Joining";

    return (
      <div className="bg-gray-900 border border-blue-900 rounded-2xl p-6 shadow-2xl flex flex-col">
        <div className="flex flex-col items-center mb-2">
          <div className="relative w-40 h-40">
            <canvas ref={chartRef} className="w-full h-full" />
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-3xl font-bold text-blue-300">
                {deal.currentBuyers}
              </span>
              <span className="text-sm text-gray-500">
                /{deal.targetBuyers}
              </span>
              {deal.currentBuyers > deal.targetBuyers && (
                <span className="text-xs text-green-400 mt-1">+ Extra!</span>
              )}
            </div>
          </div>

          <h3 className="text-xl font-semibold text-blue-400 mt-2 text-center">
            {deal.title}
          </h3>
          <p className="text-sm text-gray-400 text-center mt-3">
            {deal.description}
          </p>

          <div className="flex items-center gap-8 mt-2 text-sm">
            <div className="flex items-center text-gray-300">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
              <span>{deal.location}</span>
            </div>
            <div className="flex items-center text-green-400">
              <FontAwesomeIcon icon={faPercentage} className="mr-2" />
              <span>{deal.estimatedSavings}</span>
            </div>
          </div>

          <div className="flex items-center mt-3 text-yellow-400 text-sm">
            <FontAwesomeIcon icon={faClock} className="mr-2" />
            <span>{deal.stage}</span>
          </div>
        </div>

        <div className="flex gap-4 mt-auto">
          <button
            onClick={() => handleJoin(deal.id)}
            disabled={!isOpen}
            className={`flex-1 py-3 rounded-lg font-semibold transition ${
              isOpen
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-800 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isOpen ? "Join Deal" : "In Progress"}
          </button>
          <button
            onClick={() => handleShare(deal)}
            className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition"
          >
            <FontAwesomeIcon icon={faShareAlt} />
            Share
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-blue-300">
      <header className="bg-gray-950 border-b border-blue-400 sticky top-0 z-40">
        <div className="mx-auto px-4 py-1 flex justify-between items-center">
          <div>
            <h1 className="text-lg lg:text-xl font-bold text-yellow-200">🤝💰 UnityDeals</h1>
            <p className="text-xs lg:text-sm text-white">
              Unite. Save. Simple. 
            </p>
          </div>
          <button
            onClick={() => setShowAbout(true)}
            className="p-1 hover:bg-gray-800 rounded-lg transition w-6"
          >
            <FontAwesomeIcon icon={faInfoCircle} size="lg" />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 py-2">
        <div className="mx-auto text-center">
          <h2 className="text-lg lg:text-xl font-bold mb-1">Pool Demand. Unlock Bulk Discounts.</h2>
          <p className="text-sm lg:text-lg text-gray-400 mb-2">{deals.length} active deals • {totalBuyers}+ buyers united</p>

          <div className="flex flex-col gap-4 mx-auto justify-center">
            <div className="relative flex-1 justify-start">
              <input
                type="text"
                placeholder="Search by city or PIN code..."
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="w-full pl-12 pr-6 py-4 bg-gray-900 border border-blue-900 rounded-xl text-white focus:outline-none focus:border-blue-500 transition"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-5 text-gray-500"
              />
            </div>
            <div className="flex-1 justify-start">
                <button
                onClick={() => setShowCustomDeal(true)}
                className="px-2 py-4 w-64 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center justify-center gap-3 transition"
                >
                <FontAwesomeIcon icon={faPlusCircle} size="lg" />
                Create Custom Deal
                </button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-4 pb-2">
        <div className="mx-auto">
          <h3 className="text-lg lg:text-xl font-semibold mb-6">Browse Categories</h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 place-items-center">
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(cat.name)}
                className={`flex flex-col items-center min-w-[80px] py-6 px-4 rounded-lg transition ${
                  selectedCategory === cat.name
                    ? "bg-blue-900 border-2 border-blue-500"
                    : "bg-gray-900 border border-gray-800 hover:border-blue-800"
                }`}
              >
                <FontAwesomeIcon
                  icon={cat.icon}
                  size="2x"
                  className="mb-4 text-blue-500"
                />
                <span className="text-sm text-center font-medium">
                  {cat.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active Deals */}
      <section className="px-4 py-4 bg-gray-950">
        <div className="mx-auto">
          <h3 className="text-lg lg:text-xl font-bold mb-2">
            Active Deals ({filteredDeals.length})
          </h3>

          {filteredDeals.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-500">
                No deals match your filters yet.
              </p>
              <p className="text-gray-400 mt-2">
                Start a custom deal — others will join!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredDeals.map((deal) => (
                <DealCard key={deal.id} deal={deal} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Modal */}
      {showAbout && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-blue-900 rounded-md p-2 w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xl font-bold text-blue-300">
                About UnityDeals
              </h3>
              <button
                onClick={() => setShowAbout(false)}
                className="text-gray-400 hover:text-white"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
            </div>

            <div className="space-y-6 text-gray-300">
              <p>
                Everything is getting more expensive — real estate, electronics,
                education, travel, furniture, cars... Buyers pay premium prices
                individually while sellers struggle to find volume.
              </p>
              <p>
                <strong>UnityDeals</strong> fixes this by pooling real demand.
                We bring verified buyers together, negotiate the best group
                rates with sellers, and handle transparent fulfillment.
              </p>

              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Real Estate:</strong> 10-20% off + freebies on group
                  bookings
                </li>
                <li>
                  <strong>Electronics:</strong> 15-30% on laptops, TVs, phones
                </li>
                <li>
                  <strong>Education:</strong> Bulk uniforms, books, furniture
                </li>
                <li>
                  <strong>Travel:</strong> 30-50% on group packages
                </li>
                <li>
                  <strong>Automobiles:</strong> Extra discounts + accessories
                </li>
                <li>
                  <strong>Sellers:</strong> Guaranteed volume, less negotiation
                </li>
              </ul>

              <h4 className="text-2xl font-semibold text-blue-400">
                How It Works
              </h4>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Browse or create a deal for anything you need</li>
                <li>Join the buyer pool — more buyers = bigger discount</li>
                <li>We negotiate with verified sellers once threshold is met</li>
                <li>Secure payment released only after delivery</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Custom Deal Modal */}
      {showCustomDeal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-purple-300">
                Create Custom Deal
              </h3>

              <button
                onClick={() => setShowCustomDeal(false)}
                className="h-8 w-8 rounded-full flex items-center justify-center
                          bg-slate-800 hover:bg-slate-700 transition"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm text-gray-300" />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleCustomSubmit} className="px-6 py-5 space-y-4">
              <select
                value={customForm.category}
                onChange={(e) =>
                  setCustomForm({ ...customForm, category: e.target.value })
                }
                required
                className="w-full h-12 rounded-xl bg-slate-800 border border-slate-700
                          px-4 text-sm text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">Select Category</option>
                {categories
                  .filter((c) => c.name !== "All Categories")
                  .map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
              </select>

              <input
                type="text"
                placeholder="Deal Title"
                value={customForm.title}
                onChange={(e) =>
                  setCustomForm({ ...customForm, title: e.target.value })
                }
                required
                className="w-full h-12 rounded-xl bg-slate-800 border border-slate-700
                          px-4 text-sm text-white focus:outline-none focus:border-purple-500"
              />

              <input
                type="text"
                placeholder="Location (optional)"
                value={customForm.location}
                onChange={(e) =>
                  setCustomForm({ ...customForm, location: e.target.value })
                }
                className="w-full h-12 rounded-xl bg-slate-800 border border-slate-700
                          px-4 text-sm text-white focus:outline-none focus:border-purple-500"
              />

              <input
                type="number"
                placeholder="Target Buyers (default 50)"
                value={customForm.target}
                onChange={(e) =>
                  setCustomForm({ ...customForm, target: e.target.value })
                }
                className="w-full h-12 rounded-xl bg-slate-800 border border-slate-700
                          px-4 text-sm text-white focus:outline-none focus:border-purple-500"
              />

              <textarea
                placeholder="Description (optional)"
                style={{height: "200px"}}
                value={customForm.description}
                onChange={(e) =>
                  setCustomForm({ ...customForm, description: e.target.value })
                }
                className="w-full rounded-xl bg-slate-800 border border-slate-700
                          px-4 py-3 text-sm text-white focus:outline-none focus:border-purple-500"
              />

              {/* Footer */}
              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowCustomDeal(false)}
                  className="flex-1 w-24 h-12 rounded-xl bg-slate-800 hover:bg-slate-700
                            text-sm font-medium transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="flex-1 w-40 h-12 rounded-xl bg-purple-600 hover:bg-purple-700
                            text-sm font-semibold transition"
                >
                  Create & Join
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}